import {generatePath} from 'react-router';
import {routePaths} from 'routes/paths';

import {getAccessLevel} from 'client/services/cookie-data-source';

import {CLIENT_PAGES} from 'client/common/config';

import {Autotask} from 'client/models/autotask/types';
import {Client} from 'client/models/client/types';
import {ACCESS_LEVEL} from 'client/models/common/constants';
import {AccessLevelType} from 'client/models/common/types';
import {InstoreTask} from 'client/models/instore-tasks/types';
import {TASK_TYPES} from 'client/models/operations/constants';

import {OPERATION_STATUSES} from './constants';
import {Operation} from './types';

export const isOperationArchived = (operation?: Operation) => {
  if (!operation) {
    return false;
  }

  return operation.status === OPERATION_STATUSES.FINISHED;
};

export const isOperationStarted = (operation?: Operation) => {
  if (!operation) {
    return false;
  }

  return Boolean(operation.with_real_participations);
};

type GenerateClientOperationRouteParams = {
  clientId: number;
  operation: Operation;
  task: Autotask | InstoreTask;
  isConfigurationBlock?: boolean;
};

const weightByLevel = {
  [ACCESS_LEVEL.ADMIN]: 3,
  [ACCESS_LEVEL.CLIENT_ADMIN]: 3,
  [ACCESS_LEVEL.NATIONAL]: 3,
  [ACCESS_LEVEL.REGION]: 2,
  [ACCESS_LEVEL.STORE]: 1,
  [ACCESS_LEVEL.LOCAL]: 1,
} as const;

export const generateClientOperationRoute = (params: GenerateClientOperationRouteParams) => {
  const {clientId, operation, task, isConfigurationBlock} = params;

  const linkCommon = {
    clientId,
    operationId: operation.id,
  };

  if (task.type === TASK_TYPES.INSTORE) {
    return generatePath(routePaths.client.InstoreTaskPage, {
      ...linkCommon,
      tab: CLIENT_PAGES.CLIENT_INSTORE_TASK_TABS.PLANNING.replace('/', ''),
      taskId: task.id,
    });
  } else if (task.type === TASK_TYPES.AUTOMATION) {
    const userAccessLevel = getAccessLevel();
    const {membership, available_membership} = operation;
    // available_membership - for case when operation is attributed to agency user
    const {access_level: operationAccessLevel} = membership || available_membership || {};

    const isSameLevelOp =
      !!operationAccessLevel &&
      !!userAccessLevel &&
      weightByLevel[userAccessLevel as AccessLevelType] === weightByLevel[operationAccessLevel];
    const isRedirectToConfigTab = isSameLevelOp || isConfigurationBlock;

    return generatePath(routePaths.client.ClientAutotaskPage, {
      ...linkCommon,
      autotaskId: task.id,
      tab:
        operation.show_configuration && operation.client_automation_task_id === task.id && isRedirectToConfigTab
          ? CLIENT_PAGES.CLIENT_AUTOTASK_TABS.CONFIGURATION.replace('/', '')
          : CLIENT_PAGES.CLIENT_AUTOTASK_TABS.PARTICIPATIONS.replace('/', ''),
    });
  }

  return '';
};

type OperationHasConfigTabParams = {
  client: Client | null;
  membershipIsEditor: boolean;
  viewUserId?: number;
  accessLevel: AccessLevelType;
};

export const operationHasConfigTab = (params: OperationHasConfigTabParams) => {
  const {client, accessLevel, viewUserId, membershipIsEditor} = params;
  const {client_network, user_access_levels, auto_configuration} = client || {};

  let result = client_network ? user_access_levels?.includes(accessLevel) : accessLevel === ACCESS_LEVEL.CLIENT_ADMIN;

  if (auto_configuration) {
    result =
      !!viewUserId && ([ACCESS_LEVEL.CLIENT_ADMIN, ACCESS_LEVEL.NATIONAL] as AccessLevelType[]).includes(accessLevel);
  }

  if (!!viewUserId && client_network && accessLevel !== ACCESS_LEVEL.LOCAL) {
    result = true;
  }

  if (result && !membershipIsEditor) {
    result = false;
  }

  return Boolean(result);
};
